import React from 'react';
import { Route, Navigate } from 'react-router-dom';
import ProtectedRoute from '@/modules/auth/ProtectedRoute';

// Lazy-loaded components
const AcademicoDashboard = React.lazy(() => import('@/pages/dashboards/AcademicoDashboard'));
const CentrosPage = React.lazy(() => import('@/modules/academic/centers/CentrosPage'));
const CenterDetailPage = React.lazy(() => import('@/modules/academic/centers/CenterDetailPage'));
const DisenoCurricularPage = React.lazy(() => import('@/modules/academic/curriculum/DisenoCurricularPage'));
const PlanBuilderWizard = React.lazy(() => import('@/modules/academic/curriculum/plans/components/PlanBuilderWizard'));
const StudyPlansList = React.lazy(() => import('@/modules/academic/curriculum/study-plans/StudyPlansList'));
const StudyPlanDetailPage = React.lazy(() => import('@/modules/academic/curriculum/study-plans/StudyPlanDetailPage'));
const CapacitacionCorporativaPage = React.lazy(() => import('@/modules/academic/corporate-training/CapacitacionCorporativaPage'));
const PlanificacionEscolarPage = React.lazy(() => import('@/modules/academic/scheduling/PlanificacionEscolarPage'));
const DisenoInstruccionalPage = React.lazy(() => import('@/modules/academic/instructional-design/DisenoInstruccionalPage'));

export const academicRoutes = [
  <Route key="/academico/dashboard" path="/academico/dashboard" element={
    <ProtectedRoute requiredPermission="academic.dashboard.view"><AcademicoDashboard /></ProtectedRoute>
  } />,

  // Centros — lista de instituciones + detalle con tabs Escuelas/Campus
  <Route key="/academico/centros" path="/academico/centros" element={
    <ProtectedRoute requiredPermission="academic.centers.view"><CentrosPage /></ProtectedRoute>
  } />,
  // Legacy redirects: rutas anteriores de tabs → lista principal
  <Route key="/academico/centros/escuelas" path="/academico/centros/escuelas" element={
    <Navigate to="/academico/centros" replace />
  } />,
  <Route key="/academico/centros/instituciones" path="/academico/centros/instituciones" element={
    <Navigate to="/academico/centros" replace />
  } />,
  <Route key="/academico/centros/unidades-academicas" path="/academico/centros/unidades-academicas" element={
    <Navigate to="/academico/centros" replace />
  } />,
  <Route key="/academico/centros/campus" path="/academico/centros/campus" element={
    <Navigate to="/academico/centros" replace />
  } />,
  <Route key="/academico/centros/aulas" path="/academico/centros/aulas" element={
    <Navigate to="/academico/centros" replace />
  } />,
  // Detalle de institución con tabs Escuelas / Campus (con aulas en expand)
  <Route key="/academico/centros/:center_id" path="/academico/centros/:center_id" element={
    <ProtectedRoute requiredPermission="academic.centers.view"><CenterDetailPage /></ProtectedRoute>
  } />,

  // Programas Incorporados (página directa, sin tabs — solo StudyPlansList)
  <Route key="/academico/programas-incorporados" path="/academico/programas-incorporados" element={
    <ProtectedRoute requiredPermission="academic.plans.view"><StudyPlansList /></ProtectedRoute>
  } />,
  <Route key="/academico/programas-incorporados/:study_plan_id" path="/academico/programas-incorporados/:study_plan_id" element={
    <ProtectedRoute requiredPermission="academic.plans.view"><StudyPlanDetailPage /></ProtectedRoute>
  } />,

  // Diseño Curricular (2 niveles + tabs — sin planes-estudios, ahora en Programas Incorporados)
  <Route key="/academico/diseno-curricular" path="/academico/diseno-curricular" element={
    <Navigate to="/academico/diseno-curricular/planes" replace />
  } />,
  <Route key="/academico/diseno-curricular/planes-estudios" path="/academico/diseno-curricular/planes-estudios" element={
    <Navigate to="/academico/programas-incorporados" replace />
  } />,
  <Route key="/academico/diseno-curricular/planes" path="/academico/diseno-curricular/planes" element={
    <ProtectedRoute requiredPermission="academic.plans.view"><DisenoCurricularPage /></ProtectedRoute>
  } />,
  <Route key="/academico/diseno-curricular/materias" path="/academico/diseno-curricular/materias" element={
    <ProtectedRoute requiredPermission="academic.plans.view"><DisenoCurricularPage /></ProtectedRoute>
  } />,
  // Rutas de detalle — viven fuera del tab container
  <Route key="/academico/diseno-curricular/planes/crear" path="/academico/diseno-curricular/planes/crear" element={
    <ProtectedRoute requiredPermission="academic.plans.create"><PlanBuilderWizard /></ProtectedRoute>
  } />,

  // Diseño Instruccional — Gobernanza Pedagógica (4 tabs)
  <Route key="/academico/diseno-instruccional" path="/academico/diseno-instruccional" element={
    <Navigate to="/academico/diseno-instruccional/metodologia" replace />
  } />,
  <Route key="/academico/diseno-instruccional/metodologia" path="/academico/diseno-instruccional/metodologia" element={
    <ProtectedRoute requiredPermission="academic.instructional_design.view"><DisenoInstruccionalPage /></ProtectedRoute>
  } />,
  <Route key="/academico/diseno-instruccional/parametros" path="/academico/diseno-instruccional/parametros" element={
    <ProtectedRoute requiredPermission="academic.instructional_design.view"><DisenoInstruccionalPage /></ProtectedRoute>
  } />,
  <Route key="/academico/diseno-instruccional/distribucion" path="/academico/diseno-instruccional/distribucion" element={
    <ProtectedRoute requiredPermission="academic.instructional_design.view"><DisenoInstruccionalPage /></ProtectedRoute>
  } />,
  <Route key="/academico/diseno-instruccional/modos" path="/academico/diseno-instruccional/modos" element={
    <ProtectedRoute requiredPermission="academic.instructional_design.view"><DisenoInstruccionalPage /></ProtectedRoute>
  } />,

  // Capacitación Corporativa (2 niveles + tabs)
  <Route key="/academico/capacitacion-corporativa" path="/academico/capacitacion-corporativa" element={
    <Navigate to="/academico/capacitacion-corporativa/rutas-aprendizaje" replace />
  } />,
  <Route key="/academico/capacitacion-corporativa/rutas-aprendizaje" path="/academico/capacitacion-corporativa/rutas-aprendizaje" element={
    <ProtectedRoute requiredPermission="academic.projects.view"><CapacitacionCorporativaPage /></ProtectedRoute>
  } />,
  <Route key="/academico/capacitacion-corporativa/talleres" path="/academico/capacitacion-corporativa/talleres" element={
    <ProtectedRoute requiredPermission="academic.projects.view"><CapacitacionCorporativaPage /></ProtectedRoute>
  } />,

  // Planificación Escolar (2 niveles + tabs)
  <Route key="/academico/planificacion" path="/academico/planificacion" element={
    <Navigate to="/academico/planificacion/periodos" replace />
  } />,
  <Route key="/academico/planificacion/periodos" path="/academico/planificacion/periodos" element={
    <ProtectedRoute requiredPermission="academic.periods.view"><PlanificacionEscolarPage /></ProtectedRoute>
  } />,
  <Route key="/academico/planificacion/carreras" path="/academico/planificacion/carreras" element={
    <ProtectedRoute requiredPermission="academic.periods.view"><PlanificacionEscolarPage /></ProtectedRoute>
  } />,
  <Route key="/academico/planificacion/capacitaciones" path="/academico/planificacion/capacitaciones" element={
    <ProtectedRoute requiredPermission="academic.periods.view"><PlanificacionEscolarPage /></ProtectedRoute>
  } />,
];
